import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError, timer, TimeoutError } from 'rxjs';
import { switchMap, map, filter, take, timeout, catchError } from 'rxjs/operators';

import {FormMasterItemConfigurationModel} from '../../../../models/forms/form-master-item-configuration.model';
import {EnumValidValueModel} from '../../../../models/enum/enum-valid-value.model';
import {FormItemConfigurationModel} from '../../../../models/forms/form-item-configuration.model';
import {FormParagraphConfigurationModel} from '../../../../models/forms/form-paragraph-configuration.model';
import {FormVersionConfigurationModel} from '../../../../models/forms/form-version-configuration.model';
import {FormConfigurationModel} from '../../../../models/forms/form-configuration.model';
import {FormItemActionConfigurationModel} from '../../../../models/forms/form-item-action-configuration.model';
import {UserService} from '../../../../services/user.service';
import {FormDeleteResponseModel} from '../../../../models/forms/form-delete-response.model';
import {
  AiDraftJobStarted,
  AiDraftJobStatus,
  AiGenerateFormDraftRequest,
  AiGenerateFormDraftResponse
} from '../models/ai-form-draft.model';

// Sentinel substring the backend puts in `warnings` when the AI provider is unreachable and it
// returned a deterministic placeholder draft instead of a real one. Must stay in sync with
// TDOFormManager.GenerateDraftAsync ("AI provider unavailable. A deterministic fallback draft ...").
export const AI_PROVIDER_UNAVAILABLE_WARNING = 'AI provider unavailable';

// Lowercase substrings identifying the backend notices that mean part of the source document never
// made it into the draft — the over-page-cap truncation (PDF and Word wording) and the
// unsplittable-PDF fallback. Must stay in sync with the notices raised in
// TDOFormManager.TryGenerateFromAnthropic.
//
// These get lifted out of the review-notes panel and onto the dialog's notice banner as well. The
// panel is collapsible and, on the large imports where truncation actually happens, the notice
// lands among dozens of other entries — which is precisely the case where the user most needs to
// know that content is missing from what they're about to save.
export const CONTENT_LOSS_WARNING_MARKERS: readonly string[] = [
  'pages; only the first',
  'too long; only the first',
  'could not be split into pages'
];

// User-facing message shown when the AI assistant is down, so the placeholder form isn't presented
// as if the AI actually produced it. Also used for transport-level failures (offline, blocked
// request, 5xx) so an unreachable service always reads the same way to the user.
export const AI_UNAVAILABLE_MESSAGE = 'We couldn\'t reach the AI service. Please try again.';

// Shown when the request that STARTS the job never came back. Distinct from the generation timeout
// below: nothing was generated, so retrying is the only useful advice. The PDF wording calls out the
// upload because that is what the start request actually spends its time on — see
// startTimeoutMsFor().
export const AI_START_TIMEOUT_MESSAGE =
  'Couldn\'t upload the document — the request timed out. Check your connection and try again.';

// Edit-mode counterpart: the payload is small, so a stall here is a stall, not a slow upload.
export const AI_EDIT_START_TIMEOUT_MESSAGE = 'Couldn\'t start the request — it timed out. Please try again.';

// Shown when the job started but never finished inside the poll window. Deliberately different from
// both AI_UNAVAILABLE_MESSAGE and the backend's rate-limit copy, and actionable: page count is the
// dominant cost, so splitting the document is the fix that works.
export const AI_GENERATION_TIMEOUT_MESSAGE =
  'The AI import didn\'t finish in time. Large or scanned documents can exceed the limit — try splitting the document into fewer pages and importing again.';

/**
 * Maps an HTTP failure from the AI draft endpoints to a message worth showing a user.
 *
 * Angular's HttpErrorResponse.message is developer text ("Http failure response for
 * /api/forms/ai/generate-form-draft: 0 Unknown Error"), so it must never reach the UI. A backend
 * `errorMessage` is written for users and is preferred when present; everything else — offline,
 * blocked request (status 0), or a server fault (5xx) — collapses to AI_UNAVAILABLE_MESSAGE.
 * Callers should still log the raw error for diagnosis.
 */
export function describeAiError(err: any): string {
  const backendMessage = (err?.error?.errorMessage || '').trim();
  if (backendMessage) {
    return backendMessage;
  }

  const status = Number(err?.status);
  if (!status || status === 0 || status >= 500) {
    return AI_UNAVAILABLE_MESSAGE;
  }

  return 'Failed to generate the form. Please try again.';
}

@Injectable({
  providedIn: 'root'
})
export class FormsBuilderPolishedService {
  constructor(
    private http: HttpClient
  ) {}

  getCfgFormSimpleList(): Observable<any[]> {
    return this.http.get<any[]>('/api/forms/getCfgFormList', UserService.authorizationHeaderObject);
  }

  getNewGuids(count: number): Observable<any[]> {
    return this.http.get<any[]>(`/api/forms/getNewGuids/${count}`, UserService.authorizationHeaderObject);
  }

  saveDuplicateCfgVersion(cfgVersion: FormVersionConfigurationModel): Observable < FormConfigurationModel > {
    return this.http.post<FormConfigurationModel>('api/forms/saveDuplicateCfgVersion', cfgVersion, UserService.authorizationHeaderObject);
  }

  saveDuplicatedCfgDForm(cfgDForm: FormConfigurationModel): Observable<FormConfigurationModel> {
    return this.http.post<FormConfigurationModel>('api/forms/saveDuplicatedCfgDForm', cfgDForm, UserService.authorizationHeaderObject);
  }

  searchForFormByKeyword(keyword: string): Observable<any[]> {
    return this.http.post<any>('/api/forms/searchForFormByKeyword', { keyword }, UserService.authorizationHeaderObject);
  }

  setBuilderForm(form: any) {
    localStorage.setItem('builder-form', JSON.stringify(form));
  }

  clearBuilderForm() {
    localStorage.removeItem('builder-form');
  }

  getBuilderForm() {
    return JSON.parse(localStorage.getItem('builder-form'));
  }

  getAllValidValues(): Observable<EnumValidValueModel[]> {
    return this.http.get<EnumValidValueModel[]>('/api/valid-values-v2/getValidValues', UserService.authorizationHeaderObject);
  }

  getAllActions(): Observable<FormItemActionConfigurationModel[]> {
    return this.http.get<FormItemActionConfigurationModel[]>('/api/forms/getActions', UserService.authorizationHeaderObject);
  }

  saveCfgDForm(cfgForm: FormConfigurationModel): Observable<FormConfigurationModel> {
    return this.http.post<FormConfigurationModel>('/api/forms/saveCfgForm', cfgForm, UserService.authorizationHeaderObject);
  }

  deleteCfgForm(DFormId: string) {
    return this.http.get<FormDeleteResponseModel>(`/api/forms/delete-cfg-form/${DFormId}`);
  }

  saveCfgFormVersion(cfgVersion: FormVersionConfigurationModel): Observable<FormVersionConfigurationModel> {
    return this.http.post<FormVersionConfigurationModel>('/api/forms/saveCfgFormVersion', cfgVersion, UserService.authorizationHeaderObject);
  }

  saveCfgParagraph(cfgParagraph: FormParagraphConfigurationModel): Observable<FormParagraphConfigurationModel> {
    return this.http.post<FormParagraphConfigurationModel>('/api/forms/saveCfgParagraph', cfgParagraph, UserService.authorizationHeaderObject);
  }

  saveCfgItem(cfgItem: FormItemConfigurationModel): Observable<FormItemConfigurationModel> {
    return this.http.post<FormItemConfigurationModel>('/api/forms/saveCfgItem', cfgItem, UserService.authorizationHeaderObject);
  }

  saveCfgMasterItem(cfgMasterItem: FormMasterItemConfigurationModel): Observable<FormMasterItemConfigurationModel> {
    return this.http.post<FormMasterItemConfigurationModel>('/api/forms/saveCfgMasterItem', cfgMasterItem, UserService.authorizationHeaderObject);
  }

  /**
   * True when a "successful" draft response is actually the backend's deterministic placeholder,
   * produced because the AI provider was unreachable. Callers should surface AI_UNAVAILABLE_MESSAGE
   * and avoid applying the placeholder draft as if it were a real AI result.
   */
  isAiProviderUnavailable(response: AiGenerateFormDraftResponse | null | undefined): boolean {
    return !!response?.warnings?.some(w => (w || '').includes(AI_PROVIDER_UNAVAILABLE_WARNING));
  }

  generateAiFormDraft(request: AiGenerateFormDraftRequest): Observable<AiGenerateFormDraftResponse> {
    // The start request needs its own timeout: the poll timeout below lives INSIDE the switchMap and
    // therefore never starts until a jobId comes back, which left a stalled start request hanging
    // indefinitely behind a spinning progress bar.
    const startTimeoutMs = FormsBuilderPolishedService.startTimeoutMsFor(request);

    return this.http.post<AiDraftJobStarted>(
      '/api/forms/ai/generate-form-draft',
      request,
      UserService.authorizationHeaderObject
    ).pipe(
      timeout(startTimeoutMs),
      catchError(err => {
        if (err instanceof TimeoutError) {
          return of<AiGenerateFormDraftResponse>({
            success: false,
            errorMessage: request.mode === 'pdf' ? AI_START_TIMEOUT_MESSAGE : AI_EDIT_START_TIMEOUT_MESSAGE,
            warnings: [],
            unmappedTerms: []
          });
        }
        return throwError(() => err);
      }),
      switchMap(started => {
        // The start-timeout above short-circuits to a finished response — pass it straight through
        // rather than treating its missing jobId as a start failure.
        if (this.isDraftResponse(started)) {
          return of(started);
        }
        if (!started?.jobId) {
          return throwError(() => new Error(started?.error || 'Failed to start draft generation.'));
        }
        // Poll draft-status until the job finishes, but give up after a bounded window so a
        // stuck/slow job doesn't spin the progress UI forever. Only the timeout is swallowed
        // into a friendly result; real HTTP errors still propagate to the caller.
        //
        // Must stay above the WORST-CASE legitimate run, not just above one provider call. A PDF
        // import is capped at FormAiMaxPdfPages (20) pages and processed in waves of
        // FormAiMaxPageConcurrency (3), each wave bounded by FormAiProviderTimeoutSeconds (270s),
        // so a slow long document legitimately runs for many minutes. At the previous 300s the
        // client abandoned those generations and reported a generic failure — which also hid the
        // backend's over-20-page truncation warning, since the client gave up before it arrived.
        const POLL_TIMEOUT_MS = 8 * 60 * 1000;
        return timer(3000, 4000).pipe(
          switchMap(() => this.http.get<AiDraftJobStatus>(
            `/api/forms/ai/draft-status/${started.jobId}`,
            UserService.authorizationHeaderObject
          )),
          filter(job => job.status === 'complete' || job.status === 'error'),
          take(1),
          map(job => job.result || { success: false, errorMessage: job.errorMessage || 'Unknown error', warnings: [], unmappedTerms: [] }),
          timeout(POLL_TIMEOUT_MS),
          catchError(err => {
            if (err instanceof TimeoutError) {
              return of<AiGenerateFormDraftResponse>({
                success: false,
                errorMessage: AI_GENERATION_TIMEOUT_MESSAGE,
                warnings: [],
                unmappedTerms: []
              });
            }
            return throwError(() => err);
          })
        );
      })
    );
  }

  // Enqueue-only work on the server, plus round-trip latency. StartJob reserves a rate-limit slot,
  // registers the job and hands the generation to a background task before responding, so the server
  // itself is never the slow part of a start request.
  private static readonly START_TIMEOUT_BASE_MS = 60 * 1000;

  // Pessimistic floor throughput (~400 kbps) used to budget upload time for a PDF start request.
  // Deliberately well below any healthy connection: overshooting costs a slow user nothing, while
  // undershooting aborts a request that was going to succeed (see startTimeoutMsFor).
  private static readonly START_MIN_UPLOAD_BYTES_PER_SEC = 50 * 1024;

  // Backstop so a genuinely dead connection still surfaces an error in a bounded time. Comfortably
  // above the budget a max-size 10 MB PDF earns (~5.5 min) so it only bites on absurd payloads.
  private static readonly START_TIMEOUT_CAP_MS = 8 * 60 * 1000;

  /**
   * Timeout for the job-start POST, which must cover UPLOAD time as well as the server's response:
   * RxJS `timeout` runs from subscription and HttpClient only emits once the whole request has been
   * sent and answered.
   *
   * An edit request is a few KB of JSON, so the flat base timeout is generous and a breach really is
   * a stall. An import request carries up to MAX_FILE_BYTES (10 MB) as base64 — ~13.3 MB on the wire —
   * which needs ~1.8 Mbps sustained upload to clear the base timeout. Practices below that were
   * having healthy imports reported as "couldn't start", and the retry is not free: the server has
   * already taken a rate-limit slot (FormAiMaxConcurrent) and its job keeps running, so repeated
   * false failures burn the hourly quota and eventually surface a spurious "service is busy".
   *
   * So PDF requests get the base timeout plus an upload allowance derived from the actual payload:
   * a small PDF stays near the short timeout, a max-size one gets the room it needs.
   */
  private static startTimeoutMsFor(request: AiGenerateFormDraftRequest): number {
    const base = FormsBuilderPolishedService.START_TIMEOUT_BASE_MS;
    if (request?.mode !== 'pdf') {
      return base;
    }

    // base64 is single-byte ASCII, so its length is the payload size on the wire.
    const payloadBytes = request.pdfBase64?.length || 0;
    const uploadAllowanceMs =
      (payloadBytes / FormsBuilderPolishedService.START_MIN_UPLOAD_BYTES_PER_SEC) * 1000;

    return Math.min(
      FormsBuilderPolishedService.START_TIMEOUT_CAP_MS,
      base + Math.ceil(uploadAllowanceMs)
    );
  }

  /**
   * Distinguishes an already-finished draft response from the job-start envelope. Both flow through
   * the same switchMap because the start-request timeout is handled as a value, not an error.
   */
  private isDraftResponse(value: any): value is AiGenerateFormDraftResponse {
    return !!value && typeof value.success === 'boolean' && !value.jobId;
  }

  validateAiFormDraft(request: AiGenerateFormDraftRequest): Observable<AiGenerateFormDraftResponse> {
    return this.http.post<AiGenerateFormDraftResponse>(
      '/api/forms/ai/validate-form-draft',
      request,
      UserService.authorizationHeaderObject
    );
  }
}
